import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Plus, Shield } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import {
  useGetCocLinkQuery,
  useUnlinkCocMutation,
  useVerifyCocLinkMutation,
} from '@/services/qclansApi';
import { classifyQclansError, qclansErrorKey } from '@/services/qclansErrors';
import { formatDateShort } from '@/lib/format';

/**
 * CoC account linking (`POST /v1/link/coc/verify`). The CoC API has no OAuth —
 * ownership is proven with the one-time in-game API token. Supports multiple
 * accounts/alts; existing ones are listed with an unlink action.
 */
export function CocLink() {
  const { t } = useTranslation('coc');
  const haptics = useHaptics();

  const linkQuery = useGetCocLinkQuery();
  const accounts = linkQuery.data?.accounts ?? [];

  const [verify, { isLoading: verifying }] = useVerifyCocLinkMutation();
  const [unlink, { isLoading: unlinking }] = useUnlinkCocMutation();

  const [playerTag, setPlayerTag] = useState('');
  const [token, setToken] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onVerify = async () => {
    const tag = playerTag.trim();
    const tok = token.trim();
    if (!tag || !tok) return;
    haptics.impact('medium');
    setError(null);
    try {
      await verify({ playerTag: tag, token: tok }).unwrap();
      haptics.notification('success');
      setPlayerTag('');
      setToken('');
      setShowForm(false);
    } catch (err) {
      haptics.notification('error');
      setError(t(qclansErrorKey(classifyQclansError(err))));
    }
  };

  const onUnlink = async (tag: string) => {
    haptics.selection();
    try {
      await unlink({ playerTag: tag }).unwrap();
      haptics.notification('success');
    } catch {
      haptics.notification('error');
    }
  };

  const hasAccounts = accounts.length > 0;
  const formOpen = showForm || !hasAccounts;

  return (
    <div className="flex flex-col gap-5 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Shield aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('link.title')}</h1>
        </div>
        <p className="text-sm text-fg-muted">{t('link.intro')}</p>
      </header>

      {/* Linked accounts */}
      {linkQuery.isLoading ? (
        <div className="flex h-20 items-center justify-center">
          <Loader />
        </div>
      ) : hasAccounts ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
            {t('link.linkedTitle')}
          </h2>
          {accounts.map((acc) => (
            <div
              key={acc.playerTag}
              className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-success/15 text-success">
                <CheckCircle2 aria-hidden className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-fg">{acc.playerName}</p>
                <p className="text-[11px] tabular-nums text-fg-muted">
                  {acc.playerTag} · {t('link.verifiedAt', { date: formatDateShort(acc.verifiedAt) })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void onUnlink(acc.playerTag)}
                disabled={unlinking}
                className="shrink-0 rounded-lg bg-bg-tertiary px-3 py-2 text-xs font-bold uppercase tracking-wide text-fg-muted transition-opacity active:opacity-80 disabled:opacity-50"
              >
                {unlinking ? t('link.unlinking') : t('link.unlink')}
              </button>
            </div>
          ))}
        </section>
      ) : null}

      {/* "Where to find the token" instructions */}
      <section className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-4">
        <h2 className="text-xs font-bold uppercase tracking-wider text-fg-muted">
          {t('link.howToTitle')}
        </h2>
        <ol className="flex flex-col gap-1 text-sm text-fg">
          <li>1. {t('link.howTo1')}</li>
          <li>2. {t('link.howTo2')}</li>
          <li>3. {t('link.howTo3')}</li>
        </ol>
        <p className="text-[11px] text-fg-muted">{t('link.howToNote')}</p>
      </section>

      {/* Verify form */}
      {formOpen ? (
        <section className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
              {t('link.playerTagLabel')}
            </span>
            <input
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              value={playerTag}
              onChange={(e) => setPlayerTag(e.target.value)}
              placeholder={t('link.playerTagPlaceholder')}
              className="rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
              {t('link.tokenLabel')}
            </span>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={t('link.tokenPlaceholder')}
              className="rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={() => void onVerify()}
            disabled={verifying || !playerTag.trim() || !token.trim()}
            className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
          >
            {verifying ? t('link.submitting') : t('link.submit')}
          </button>
          {error && (
            <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
              {error}
            </p>
          )}
        </section>
      ) : (
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            setShowForm(true);
          }}
          className="flex items-center justify-center gap-2 self-start rounded-xl bg-bg-tertiary px-4 py-2.5 text-sm font-bold text-brand transition-opacity active:opacity-80"
        >
          <Plus aria-hidden className="h-4 w-4" />
          {t('link.addAnother')}
        </button>
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}
